import SettingsPersistence from "./settingsPersistence";
import { KeywordsAdapter } from "../llm/keywordsAdapter";
import LLMService from "../llm/llm";
import { SimpleAuthManager } from "../llm/authManager";
import { AgentRegistry } from "../agentRegistry";
import { getContextResolver } from "../core/contextResolver";
import { getConversationPersistence, PERSISTENCE_SCHEMA_VERSION, } from '../persistence';
import { addCurrentPresentationSearchResults, addPresentationLibrarySearchResults, createEmptySearchHistory, normalizeSearchHistory, rebuildSearchHistoryFromMessages, } from './searchHistory';
/**
 * Global State Manager
 * Manages in-memory state for settings, conversations, and agent context
 * Architecture: TypeScript-as-leader for settings synchronization
 */
class GlobalStateManager {
    constructor() {
        /**
         * Map of adapter name -> adapter instance
         */
        this._adapters = new Map();
        /**
         * Context ID -> content mappings (for token-efficient context passing)
         * Architecture: Used for Context ID Passing System
         */
        this._contextIdMappings = new Map();
        /**
         * Current JWT token (from AuthService)
         */
        this._jwtToken = null;
        /**
         * Auth manager for getting temporary API keys
         * Architecture: Converts JWT tokens → API keys for external services
         */
        this._authManager = null;
        /**
         * LLM Service instance
         * Architecture: Main interface for making LLM requests
         */
        this._llmService = null;
        /**
         * Initialization flag
         */
        this._isInitialized = false;
        this.conversationRegistry = {};
        this.contextResolver = getContextResolver();
        this._settingsPersistence = SettingsPersistence.getInstance();
        this._conversationPersistence = getConversationPersistence();
    }
    static getInstance() {
        if (!GlobalStateManager.instance) {
            GlobalStateManager.instance = new GlobalStateManager();
        }
        return GlobalStateManager.instance;
    }
    /**
     * Initialize the global state manager
     * Architecture: Single entry point - initializes everything
     * - Loads settings from persistence
     * - Creates auth manager
     * - Initializes LLM system with adapters
     * - Sets up default model from settings
     */
    async initialize(jwtToken) {
        if (this._isInitialized) {
            console.log('[GlobalStateManager] Already initialized');
            return;
        }
        // 1. Load settings from persistence
        await this._settingsPersistence.initialize();
        console.log('[GlobalStateManager] Settings loaded');
        // 2. Set JWT token if provided
        if (jwtToken) {
            this.setJwtToken(jwtToken);
        }
        // 3. Create auth manager (for API key retrieval)
        this._authManager = this.createAuthManager();
        console.log('[GlobalStateManager] Auth manager created');
        // 4. Initialize LLM system
        await this.initializeLLM();
        console.log('[GlobalStateManager] LLM system initialized');
        // 5. Cleanup old conversations based on retention setting
        await this.cleanupOldConversations();
        this._isInitialized = true;
        console.log('[GlobalStateManager] Fully initialized');
    }
    /**
     * Cleanup old conversations based on retention setting
     * Called during initialization
     */
    async cleanupOldConversations() {
        try {
            const retentionDays = this.getConversationRetentionDays();
            const deleted = await this._conversationPersistence.cleanupOldConversations(retentionDays);
            if (deleted > 0) {
                console.log(`[GlobalStateManager] Cleaned up ${deleted} old conversations`);
            }
        }
        catch (error) {
            // Don't fail initialization if cleanup fails
            console.warn('[GlobalStateManager] Failed to cleanup old conversations:', error);
        }
    }
    /**
     * Create auth manager instance
     * Architecture: Handles JWT → API key conversion
     */
    createAuthManager() {
        return new SimpleAuthManager();
    }
    /**
     * Initialize LLM system
     * Architecture: Creates adapters and LLM service
     */
    async initializeLLM() {
        if (!this._authManager) {
            throw new Error('Auth manager must be created before initializing LLM');
        }
        // Get model from settings (chatModel is the primary model for the LLM service)
        const modelSettings = this.getModelSettings();
        const defaultModel = modelSettings.chatModel;
        // Create and register Keywords adapter
        const keywordsAdapter = new KeywordsAdapter(this._authManager, this._jwtToken || undefined);
        this.registerAdapter(keywordsAdapter);
        console.log('[GlobalStateManager] Keywords adapter registered');
        // Create LLM service
        this._llmService = new LLMService();
        this._llmService.model = defaultModel;
        console.log('[GlobalStateManager] LLM service created with model:', defaultModel);
    }
    /**
     * Get LLM service instance
     * Architecture: Main interface for LLM requests
     */
    getLLMService() {
        if (!this._llmService) {
            throw new Error('LLM service not initialized. Call initialize() first.');
        }
        return this._llmService;
    }
    /**
     * Get auth manager instance
     */
    getAuthManager() {
        if (!this._authManager) {
            throw new Error('Auth manager not initialized. Call initialize() first.');
        }
        return this._authManager;
    }
    /**
     * Check if auth manager is ready (non-throwing alternative to getAuthManager)
     */
    isAuthReady() {
        return this._authManager !== null && this._jwtToken !== null;
    }
    /**
     * Context Registry
     */
    getContextItems(items, contextDocumentId) {
        return this.contextResolver.resolveContextItems(items, contextDocumentId);
    }
    getContext(contextId, contextDocumentId) {
        const resolved = this.contextResolver.resolve(contextId, contextDocumentId);
        return resolved?.content;
    }
    addOrUpdateContext(contextId, context) {
        this.contextResolver.storeWithId(contextId, context);
    }
    // Conversation Registry
    getConversation(conversationId) {
        return this.conversationRegistry[conversationId];
    }
    addOrUpdateConversation(conversationId, conversation) {
        conversation.searchHistory = normalizeSearchHistory(conversation.searchHistory);
        this.conversationRegistry[conversationId] = conversation;
    }
    /**
     * Create a new conversation with the given ID, presentation ID, and origin
     * Initializes a fresh AgentRegistry for the conversation
     */
    createConversation(conversationId, documentId, conversationOrigin, documentType = "unknown") {
        const agentRegistry = new AgentRegistry();
        const conversation = {
            id: conversationId,
            agentRegistry,
            documentId,
            documentType,
            presentationId: documentId,
            conversationOrigin,
            searchHistory: createEmptySearchHistory(),
        };
        this.conversationRegistry[conversationId] = conversation;
        console.log(`[GlobalStateManager] Created conversation: ${conversationId} with documentId: ${documentId}, origin: ${conversationOrigin}`);
        return conversation;
    }
    /**
     * Get normalized conversation search history.
     */
    getConversationSearchHistory(conversationId) {
        const conversation = this.conversationRegistry[conversationId];
        if (!conversation) {
            return createEmptySearchHistory();
        }
        conversation.searchHistory = normalizeSearchHistory(conversation.searchHistory);
        return conversation.searchHistory;
    }
    /**
     * Replace conversation search history.
     */
    setConversationSearchHistory(conversationId, searchHistory) {
        const conversation = this.conversationRegistry[conversationId];
        if (!conversation) {
            return;
        }
        conversation.searchHistory = normalizeSearchHistory(searchHistory);
    }
    /**
     * Record current-presentation search results for dedupe.
     */
    recordCurrentPresentationSearchResults(conversationId, documentId, results) {
        const conversation = this.conversationRegistry[conversationId];
        if (!conversation) {
            return;
        }
        conversation.searchHistory = normalizeSearchHistory(conversation.searchHistory);
        addCurrentPresentationSearchResults(conversation.searchHistory, documentId, results);
    }
    /**
     * Record presentation-library search results for dedupe.
     */
    recordPresentationLibrarySearchResults(conversationId, results) {
        const conversation = this.conversationRegistry[conversationId];
        if (!conversation) {
            return;
        }
        conversation.searchHistory = normalizeSearchHistory(conversation.searchHistory);
        addPresentationLibrarySearchResults(conversation.searchHistory, results);
    }
    /**
     * Remove a conversation from the registry
     * Called when a conversation/job is ended or cleaned up
     */
    removeConversation(conversationId) {
        if (this.conversationRegistry[conversationId]) {
            delete this.conversationRegistry[conversationId];
            console.log(`[GlobalStateManager] Removed conversation: ${conversationId}`);
            return true;
        }
        return false;
    }
    // =========================================================================
    // CONVERSATION PERSISTENCE
    // =========================================================================
    /**
     * Get the conversation persistence service
     */
    getConversationPersistence() {
        return this._conversationPersistence;
    }
    /**
     * Persist a conversation to disk
     * Extracts all data from the in-memory conversation and saves it
     *
     * @param conversationId - The conversation to persist
     * @param options - Additional options like title, planId, status
     */
    async persistConversation(conversationId, options) {
        const conversation = this.conversationRegistry[conversationId];
        if (!conversation) {
            console.warn(`[GlobalStateManager] Cannot persist - conversation not found: ${conversationId}`);
            return;
        }
        // Build the persisted conversation object
        const persisted = this.buildPersistedConversation(conversation, options);
        // Save to disk
        await this._conversationPersistence.saveConversation(persisted);
        console.log(`[GlobalStateManager] Persisted conversation: ${conversationId}`);
    }
    /**
     * Build a PersistedConversation from an in-memory Conversation
     */
    buildPersistedConversation(conversation, options) {
        const now = new Date().toISOString();
        // Convert origin to persisted format
        let origin = conversation.conversationOrigin;
        if (conversation.conversationOrigin === 'other' && options.planId) {
            origin = 'builder';
        }
        // Extract all threads from the conversation's AgentRegistry
        const threads = {};
        const threadManager = conversation.agentRegistry.threadManager;
        // Get all threads for this conversation
        const conversationThreads = threadManager.getThreadsForConversation(conversation.id);
        for (const thread of conversationThreads) {
            threads[thread.threadId] = {
                threadId: thread.threadId,
                agentType: thread.agentType,
                conversationId: thread.conversationId,
                documentId: thread.documentId,
                messages: thread.messages,
                createdAt: thread.createdAt,
                lastActivityAt: thread.lastActivityAt,
                tokenUsage: thread.tokenUsage,
            };
        }
        // Extract context cache entries used in this conversation
        const contextCache = {};
        const contextIdsUsed = this.extractContextIdsFromMessages(conversationThreads);
        for (const contextId of contextIdsUsed) {
            const resolved = this.contextResolver.resolve(contextId);
            if (resolved) {
                contextCache[contextId] = {
                    contextId,
                    prefix: resolved.prefix,
                    content: resolved.content,
                    storedAt: resolved.storedAt || Date.now(),
                };
            }
        }
        // Extract image cache entries used in this conversation
        const imageCache = {};
        const imageIdsUsed = this.extractImageIdsFromMessages(conversationThreads);
        for (const imageId of imageIdsUsed) {
            const image = this.contextResolver.getImage(imageId);
            if (image) {
                imageCache[imageId] = {
                    imageId,
                    base64: image.base64,
                    mimeType: image.mimeType,
                    metadata: image.metadata,
                };
            }
        }
        // Ensure search history exists (lazy rebuild if loading legacy conversation state).
        const searchHistory = conversation.searchHistory
            ? normalizeSearchHistory(conversation.searchHistory)
            : rebuildSearchHistoryFromMessages(conversationThreads, conversation.documentId);
        conversation.searchHistory = searchHistory;
        // Find the earliest created and latest active timestamps
        let createdAt = now;
        let lastActiveAt = now;
        for (const thread of conversationThreads) {
            const threadCreated = new Date(thread.createdAt).toISOString();
            const threadActive = new Date(thread.lastActivityAt).toISOString();
            if (threadCreated < createdAt) {
                createdAt = threadCreated;
            }
            if (threadActive > lastActiveAt) {
                lastActiveAt = threadActive;
            }
        }
        // Count messages
        let messageCount = 0;
        for (const thread of conversationThreads) {
            messageCount += thread.messages.length;
        }
        return {
            conversationId: conversation.id,
            origin,
            title: options.title,
            documentId: conversation.documentId,
            documentType: conversation.documentType,
            planId: options.planId,
            createdAt,
            lastActiveAt,
            messageCount,
            status: options.status || 'active',
            version: PERSISTENCE_SCHEMA_VERSION,
            threads,
            contextCache,
            imageCache,
            searchHistory,
            buildProgress: options.buildProgress,
        };
    }
    /**
     * Extract context IDs referenced in messages
     */
    extractContextIdsFromMessages(threads) {
        const contextIds = new Set();
        const contextIdPattern = /\b(as|logo|color|wikimedia|artifact|slide|component|layout|asset|page|excelread|excel)_[a-z0-9]+\b/gi;
        for (const thread of threads) {
            for (const message of thread.messages) {
                const content = typeof message.content === 'string'
                    ? message.content
                    : JSON.stringify(message.content);
                const matches = content.match(contextIdPattern);
                if (matches) {
                    for (const match of matches) {
                        contextIds.add(match.toLowerCase());
                    }
                }
            }
        }
        return contextIds;
    }
    /**
     * Extract image IDs referenced in messages (from image_url content parts)
     */
    extractImageIdsFromMessages(threads) {
        const imageIds = new Set();
        for (const thread of threads) {
            for (const message of thread.messages) {
                if (Array.isArray(message.content)) {
                    for (const part of message.content) {
                        if (part.type === 'image_url' && part.image_url?.url) {
                            // Extract ID from data URL or reference
                            // Images in cache are referenced by their context ID (logo_*, wikimedia_*, etc.)
                            const url = part.image_url.url;
                            if (url.includes('_')) {
                                // Try to extract a context ID from the URL if it's embedded
                                const idMatch = url.match(/\b(logo|wikimedia|asset)_[a-z0-9]+\b/i);
                                if (idMatch) {
                                    imageIds.add(idMatch[0].toLowerCase());
                                }
                            }
                        }
                    }
                }
            }
        }
        // Also include all images currently in the cache that match known prefixes
        const cachedImageIds = this.contextResolver.getImageIds();
        for (const id of cachedImageIds) {
            imageIds.add(id);
        }
        return imageIds;
    }
    /**
     * Load a persisted conversation into memory
     * Restores all threads and context data
     *
     * @param conversationId - The persisted conversation ID to load
     * @returns The restored in-memory Conversation, or null if not found
     */
    async loadPersistedConversation(conversationId) {
        console.log(`[GlobalStateManager] Loading persisted conversation: ${conversationId}`);
        const loadResult = await this._conversationPersistence.loadConversationForRestore(conversationId);
        if (!loadResult) {
            console.log(`[GlobalStateManager] Persisted conversation not found: ${conversationId}`);
            return null;
        }
        // Map persisted origin back to in-memory origin
        const validOrigins = ['pptTaskpane', 'excelTaskpane', 'planning', 'builder', 'mainTaskpane', 'other'];
        const conversationOrigin = validOrigins.includes(loadResult.metadata.origin)
            ? loadResult.metadata.origin
            : 'other';
        // Create a new conversation in the registry
        const documentType = loadResult.metadata.documentType || 'unknown';
        const conversation = this.createConversation(conversationId, loadResult.metadata.documentId, conversationOrigin, documentType);
        // Restore threads to the ThreadManager using restoreThread()
        // This preserves original thread IDs for proper conversation continuity
        const threadManager = conversation.agentRegistry.threadManager;
        for (const [_threadId, threadState] of loadResult.threads) {
            threadManager.restoreThread(threadState, false);
        }
        // Restore context entries to ContextResolver
        for (const [contextId, entry] of loadResult.contextEntries) {
            this.contextResolver.storeWithId(contextId, entry.content);
        }
        // Restore images to ContextResolver
        for (const [imageId, entry] of loadResult.imageEntries) {
            this.contextResolver.storeImage(imageId, entry.base64, entry.mimeType, entry.metadata);
        }
        // Restore search history (lazy rebuild for legacy persisted conversations without this field).
        if (loadResult.searchHistory) {
            conversation.searchHistory = normalizeSearchHistory(loadResult.searchHistory);
        }
        else {
            conversation.searchHistory = rebuildSearchHistoryFromMessages(loadResult.threads.values(), loadResult.metadata.documentId);
            console.log(`[GlobalStateManager] Rebuilt missing search history from thread messages: ${conversationId}`);
        }
        console.log(`[GlobalStateManager] Restored conversation: ${conversationId} with ${loadResult.threads.size} threads`);
        return conversation;
    }
    /**
     * List persisted conversations
     */
    async listPersistedConversations(filter) {
        const conversations = await this._conversationPersistence.listConversations(filter);
        return conversations.map(c => c.conversationId);
    }
    /**
     * Delete a persisted conversation
     */
    async deletePersistedConversation(conversationId) {
        return this._conversationPersistence.deleteConversation(conversationId);
    }
    // JWT token implementation
    getJwtToken() {
        return this._jwtToken;
    }
    async setJwtToken(token) {
        this._jwtToken = token;
        // If already initialized, update the existing adapter (triggers refresh loop start/stop)
        if (this._isInitialized && this._authManager) {
            // Clean up existing adapters properly (stops refresh loops, etc.)
            await this.clearAdapters();
            // Create new adapter with updated token
            const keywordsAdapter = new KeywordsAdapter(this._authManager, token || undefined);
            this.registerAdapter(keywordsAdapter);
            console.log('[GlobalStateManager] Adapters updated with new JWT token');
        }
    }
    /**
     * Get user settings
     */
    getUserSettings() {
        return this._settingsPersistence.getUserSettings();
    }
    /**
     * Update user settings
     */
    async updateUserSettings(settings) {
        await this._settingsPersistence.updateSettings(settings);
    }
    /**
     * Get all custom instructions
     */
    getCustomInstructions() {
        return this._settingsPersistence.getCustomInstructions();
    }
    /**
     * Get custom instructions for a specific category
     */
    getCustomInstructionsForCategory(category) {
        return this._settingsPersistence.getCustomInstructionsForCategory(category);
    }
    /**
     * Get active custom instructions for a category (only those with on=true)
     */
    getActiveCustomInstructions(category) {
        return this._settingsPersistence.getActiveCustomInstructions(category);
    }
    /**
     * Get concatenated active instructions as a single string (for LLM injection)
     */
    getActiveInstructionsText(category) {
        return this._settingsPersistence.getActiveInstructionsText(category);
    }
    /**
     * Update all custom instructions for a category
     */
    async updateCustomInstructions(category, instructions) {
        await this._settingsPersistence.updateCustomInstructions(category, instructions);
    }
    /**
     * Add a new custom instruction
     */
    async addCustomInstruction(category, instruction) {
        return this._settingsPersistence.addCustomInstruction(category, instruction);
    }
    /**
     * Update a specific custom instruction
     */
    async updateCustomInstruction(category, id, updates) {
        await this._settingsPersistence.updateCustomInstruction(category, id, updates);
    }
    /**
     * Delete a custom instruction
     */
    async deleteCustomInstruction(category, id) {
        await this._settingsPersistence.deleteCustomInstruction(category, id);
    }
    /**
     * Toggle a custom instruction on/off
     */
    async toggleCustomInstruction(category, id) {
        await this._settingsPersistence.toggleCustomInstruction(category, id);
    }
    /**
     * Get Excel export settings
     */
    getExcelExportSettings() {
        return this._settingsPersistence.getExcelExportSettings();
    }
    /**
     * Update Excel export settings
     */
    async updateExcelExportSettings(updates) {
        await this._settingsPersistence.updateExcelExportSettings(updates);
    }
    // =========================================================================
    // MODEL SETTINGS
    // =========================================================================
    /**
     * Get model settings
     */
    getModelSettings() {
        return this._settingsPersistence.getModelSettings();
    }
    /**
     * Update model settings
     */
    async updateModelSettings(updates) {
        await this._settingsPersistence.updateModelSettings(updates);
    }
    /**
     * Get chat model (main orchestrator)
     */
    getChatModel() {
        return this._settingsPersistence.getChatModel();
    }
    /**
     * Get reasoning mode
     */
    getReasoningMode() {
        return this._settingsPersistence.getReasoningMode();
    }
    // =========================================================================
    // GENERAL SETTINGS
    // =========================================================================
    /**
     * Get general settings
     */
    getGeneralSettings() {
        return this._settingsPersistence.getGeneralSettings();
    }
    /**
     * Update general settings
     */
    async updateGeneralSettings(updates) {
        await this._settingsPersistence.updateGeneralSettings(updates);
    }
    /**
     * Get conversation retention days (1-14)
     */
    getConversationRetentionDays() {
        return this._settingsPersistence.getConversationRetentionDays();
    }
    /**
     * Set conversation retention days (1-14)
     */
    async setConversationRetentionDays(days) {
        await this._settingsPersistence.setConversationRetentionDays(days);
    }
    // =========================================================================
    // EXPERIMENTAL SETTINGS
    // =========================================================================
    /**
     * Get experimental settings
     */
    getExperimentalSettings() {
        return this._settingsPersistence.getExperimentalSettings();
    }
    /**
     * Update experimental settings
     */
    async updateExperimentalSettings(updates) {
        await this._settingsPersistence.updateExperimentalSettings(updates);
    }
    /**
     * Get space planning mode for A/B testing
     */
    getSpacePlanningMode() {
        return this._settingsPersistence.getSpacePlanningMode();
    }
    /**
     * Set space planning mode for A/B testing
     */
    async setSpacePlanningMode(mode) {
        await this._settingsPersistence.setSpacePlanningMode(mode);
    }
    // =========================================================================
    // PROMPT LIBRARY
    // =========================================================================
    /**
     * Get all saved prompts
     */
    getPromptLibrary() {
        return this._settingsPersistence.getPromptLibrary();
    }
    /**
     * Get a specific prompt by ID
     */
    getPromptById(id) {
        return this._settingsPersistence.getPromptById(id);
    }
    /**
     * Add a new prompt to the library
     */
    async addPrompt(prompt) {
        return this._settingsPersistence.addPrompt(prompt);
    }
    /**
     * Update an existing prompt
     */
    async updatePrompt(id, updates) {
        await this._settingsPersistence.updatePrompt(id, updates);
    }
    /**
     * Delete a prompt from the library
     */
    async deletePrompt(id) {
        await this._settingsPersistence.deletePrompt(id);
    }
    /**
     * Replace all prompts in the library
     */
    async setPromptLibrary(prompts) {
        await this._settingsPersistence.setPromptLibrary(prompts);
    }
    /**
     * Register an adapter instance
     */
    registerAdapter(adapter) {
        this._adapters.set(adapter.name, adapter);
    }
    /**
     * Get an adapter by name
     */
    getAdapter(name) {
        return this._adapters.get(name);
    }
    /**
     * Get adapter for a model string
     * Format: "adapter/model" or just "model" (defaults to keywords)
     */
    getAdapterFromSettings() {
        const settings = this.getUserSettings();
        const adapterName = settings.llm?.adapter || "keywords"; // Default to "keywords"
        return this.getAdapter(adapterName);
    }
    /**
     * Remove an adapter
     */
    removeAdapter(name) {
        const adapter = this._adapters.get(name);
        if (adapter && adapter.close) {
            adapter.close();
        }
        this._adapters.delete(name);
    }
    /**
     * Clear all adapters
     */
    async clearAdapters() {
        const adapters = Array.from(this._adapters.values());
        for (const adapter of adapters) {
            if (adapter.close) {
                await adapter.close();
            }
        }
        this._adapters.clear();
    }
}
export default GlobalStateManager;
